import type { RouteRecordRaw } from 'vue-router'
import {
  collectRouteCandidates,
  toAnyPermission,
} from '@/config/businessRoutePermissions'
import { legacyTenantRedirect, maybeRouteName } from './tenantScoped'

const createCollectChildren = (withNames: boolean): RouteRecordRaw[] => [
  {
    path: 'dashboard',
    ...maybeRouteName(withNames, 'collect-dashboard'),
    component: () => import('@/views/collect/CollectDashboard.vue'),
    meta: { title: '采集中心工作台', permission: 'collect:center:view' },
  },
  {
    // 顶栏已不再展示：数据源的主入口在 Agent 详情内，这里保留为二级页，
    // 承接"未挂载数据源"入口、只有数据源权限的角色，以及存量书签
    path: 'sources',
    ...maybeRouteName(withNames, 'collect-sources'),
    component: () => import('@/views/collect/CollectSources.vue'),
    meta: { title: '数据源管理', permission: 'collect:source:view' },
  },
  {
    path: 'agents',
    ...maybeRouteName(withNames, 'collect-agents'),
    component: () => import('@/views/collect/CollectAgents.vue'),
    meta: { title: 'Agent 管理', permission: 'collect:agent:view' },
  },
  {
    path: 'agents/:id',
    ...maybeRouteName(withNames, 'collect-agent-detail'),
    component: () => import('@/views/collect/CollectAgentDetail.vue'),
    meta: { title: 'Agent 详情', permission: 'collect:agent:view' },
  },
  {
    path: 'metrics',
    ...maybeRouteName(withNames, 'collect-metrics'),
    component: () => import('@/views/collect/CollectMetrics.vue'),
    meta: { title: '指标管理', permission: 'collect:metric:view' },
  },
  {
    path: 'metrics/:id',
    ...maybeRouteName(withNames, 'collect-metric-detail'),
    component: () => import('@/views/collect/CollectMetricDetail.vue'),
    meta: { title: '指标详情', permission: 'collect:metric:view' },
  },
  {
    // 顶栏已不再展示：只从工作台异常入口、Agent 详情与指标详情的"查看全部"进，
    // 保留独立路由是因为"当前全平台有多少在失败"这类跨 Agent 排障，嵌套视图回答不了
    path: 'instances',
    ...maybeRouteName(withNames, 'collect-instances'),
    component: () => import('@/views/collect/CollectInstances.vue'),
    // 台账展示的是指标详情页"最近执行"的同一批数据，可见性一致，复用指标查看权限
    meta: { title: '采集实例', permission: 'collect:metric:view' },
  },
]

const createCollectRoute = (path: string, defaultRouteBasePath: string, withNames: boolean): RouteRecordRaw => ({
  path,
  component: () => import('@/views/collect/CollectLayout.vue'),
  ...(withNames ? {} : { beforeEnter: legacyTenantRedirect }),
  meta: {
    permission: toAnyPermission(collectRouteCandidates),
    defaultRouteBasePath,
    defaultRouteCandidates: collectRouteCandidates,
    ...(!withNames ? { legacyTenantRedirect: true } : {}),
  },
  children: createCollectChildren(withNames)
})

// 独立全屏接入向导：不套 CollectLayout 的 4-tab 顶栏。
const createOnboardingRoute = (path: string, withNames: boolean): RouteRecordRaw => ({
  path,
  ...maybeRouteName(withNames, 'collect-onboarding'),
  component: () => import('@/views/collect/onboarding/OnboardingWizard.vue'),
  ...(withNames ? {} : { beforeEnter: legacyTenantRedirect }),
  meta: {
    title: '采集接入向导',
    permission: toAnyPermission(collectRouteCandidates),
    ...(!withNames ? { legacyTenantRedirect: true } : {}),
  },
})

const collectRoutes: RouteRecordRaw[] = [
  createCollectRoute('t/:tenantCode/collect', '/t/:tenantCode/collect', true),
  createCollectRoute('collect', '/collect', false),
  createOnboardingRoute('t/:tenantCode/collect/onboarding', true),
  createOnboardingRoute('collect/onboarding', false),
]

export default collectRoutes
